import { isCasePreviewEnabled, isCaseView } from './caseFeature';
import { canUseActivePermissionAction } from './permissionPolicy';
import type { AppView, CpqState } from './types';

type ViewCapability = readonly [featureId: string, actionId: string];

const VIEW_CAPABILITIES: Partial<Record<AppView, ViewCapability>> = {
  // The showcase reuses plan visibility only as a frontend navigation guard.
  // It is not a formal Case permission or server-side authorization contract.
  cases: ['plans', 'plans:solution.read'],
  // The disposable case-admin screen uses the same temporary projection.
  'case-admin': ['plans', 'plans:solution.read'],
  workspace: ['workspace', 'workspace:quote.read'],
  quotes: ['quotes', 'quotes:quote.read'],
  plans: ['plans', 'plans:solution.read'],
  packages: ['packages', 'packages:solution.read'],
  devices: ['devices', 'devices:catalog.read'],
  customers: ['customers', 'customers:customer.read'],
  inventory: ['inventory', 'inventory:inventory.read'],
  'temp-devices': ['devices', 'devices:catalog.manage'],
  dashboard: ['quotes', 'quotes:quote.read'],
};

const FALLBACK_VIEWS: AppView[] = [
  'cases',
  'case-admin',
  'workspace',
  'quotes',
  'plans',
  'packages',
  'devices',
  'replacements',
  'customers',
  'inventory',
  'settings',
  'dashboard',
];

export function canManageGlobalSettings(state: CpqState): boolean {
  return canUseActivePermissionAction(
    state,
    'permissions',
    'permissions:identity.role.manage',
  );
}

export function canAccessSettings(state: CpqState): boolean {
  return state.catalog.users.some(
    (user) => user.id === state.currentActorUserId && user.status === 'active',
  );
}

/** The disposable state has no formal replacement grant; the server session gates it. */
export function canAccessReplacements(state: CpqState): boolean {
  return canAccessSettings(state);
}

/** Formal settings authorization is applied by the active session shell. */
export function canUsePermissionCenter(state: CpqState): boolean {
  return canAccessSettings(state);
}

export function isViewAllowedForState(state: CpqState, view: AppView): boolean {
  if (isCaseView(view) && !isCasePreviewEnabled()) return false;
  if (view === 'share') return true;
  const normalized = view === 'options' ? 'plans' : view;
  if (normalized === 'settings') return canAccessSettings(state);
  if (normalized === 'permissions') return canUsePermissionCenter(state);
  if (normalized === 'replacements') return canAccessReplacements(state);
  const capability = VIEW_CAPABILITIES[normalized];
  return Boolean(
    capability && canUseActivePermissionAction(state, ...capability),
  );
}

export function allowedViewForState(
  state: CpqState,
  activeView: AppView,
): AppView {
  if (activeView === 'share') return 'share';
  const normalized = activeView === 'options' ? 'plans' : activeView;
  if (isViewAllowedForState(state, normalized)) return normalized;
  return (
    FALLBACK_VIEWS.find((view) => isViewAllowedForState(state, view)) ??
    (isCaseView(normalized) && !isCasePreviewEnabled()
      ? 'workspace'
      : normalized)
  );
}

export function guardRoleView(state: CpqState): CpqState {
  const activeView = allowedViewForState(state, state.activeView);
  return activeView === state.activeView ? state : { ...state, activeView };
}
